import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {MinimalMemory} from "../../_types/memorybox";
import {FormControl} from "@angular/forms";
import {AuthService} from "../../_services/auth.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {debounceTime, of, switchMap, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {ContentService} from "../../_services/content.service";

@Component({
    selector: 'app-memories-search',
    standalone: false,
    templateUrl: './memories-search.component.html',
    styleUrl: './memories-search.component.scss',
})
export class MemoriesSearchComponent {
    @Input() title: string = 'Select Memory Box';
    @Input() placeholder: string = 'Select Memory Box';
    @Input() disabled: boolean = false;
    @Input() showSearchBtn: boolean = false;
    @Input({required: true}) timeline!: number;
    @Input() value: MinimalMemory | undefined;
    @Input() filter: Record<string, any> | undefined = undefined;
    @Output() memorySelect: EventEmitter<MinimalMemory | undefined> = new EventEmitter();

    isRaa: boolean = false;
    memorySearchControl: FormControl = new FormControl();
    loading: boolean = false;
    filteredMemories: MinimalMemory[] = [];

    constructor(private authService: AuthService,
                private snackbarService: SnackbarService,
                private contentService: ContentService) {


        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });
        this.memorySearchControl.valueChanges.pipe(
            debounceTime(300),
            tap((): boolean => this.loading = true),
            switchMap(value => {
                return this.contentService.getTimelineMemories(this.timeline, 0, 100000, value).pipe(
                    // Handle API errors gracefully without breaking the stream
                    catchError(e => {
                        this.snackbarService.openSnackBar(e, 'error');
                        return of({results: [] as MinimalMemory[]});
                    })
                );
            })
        ).subscribe(r => {
            this.loading = false;
            this.filteredMemories = r.results;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.value) {
            this.memorySearchControl.setValue(this.value, {emitEvent: false});
        } else {
            this.memorySearchControl.setValue(null, {emitEvent: false});
        }

        if (this.disabled) {
            this.memorySearchControl.disable({emitEvent: false});
        } else {
            this.memorySearchControl.enable({emitEvent: false});
        }

        if (changes['timeline']?.currentValue) {
            this.memorySelect.emit(undefined);
            this.memorySearchControl.setValue(null, {emitEvent: false});
            this.filteredMemories = [];
            this.contentService.getTimelineMemories(this.timeline, 0, 100000).subscribe({
                next: (r): void => {
                    if (this.filteredMemories.length == 0 && !this.loading) {
                        // only add if they havent begun searching
                        this.filteredMemories = r.results;
                    }
                },
                error: (e): void => {
                    this.snackbarService.openSnackBar(e, 'error');
                }
            });
        }
    }

    selectMemoryId(memoryId: number): void {
        const memory = this.filteredMemories.find(x => x.id === memoryId);

        if (!memory) {
            return;
        }

        this.memorySearchControl.setValue(memory, {emitEvent: false});
        this.filteredMemories = [];
        this.memorySelect.emit(memory);
    }

    displayFn = (value: MinimalMemory | number | null): string => {
        if (!value) {
            return '';
        }
        let display = ''

        let memory
        if (typeof value === 'number') {
            let fetchedMemory = this.filteredMemories.find(x => x.id === value);
            if (fetchedMemory && 'id' in fetchedMemory) {
                memory = fetchedMemory
            }
        } else {
            memory = value;
        }
        if (memory?.title) {
            display = memory.title
        }
        if (memory?.date) {
            let date = new Date(memory?.date)
            display = display + ' (' + date.toLocaleDateString('en-CA').slice(0, 10) + ')'
        }
        return display
    };

    searchAllMemories(): void {
        this.loading = true;
        this.contentService.getTimelineMemories(this.timeline, 0, 1000).subscribe({
            next: r => {
                this.loading = false;
                this.filteredMemories = r.results;
            }, error: error => {
                this.loading = false;
                this.snackbarService.openSnackBar(error, 'error')
            }
        });
    }
}
